import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { OrderPagination } from '@/views/Order/components/table/pagination';
import type { ActiveFilter } from '@/views/Order/components/table/OrderToolbar';
import i18n, { formatNumber } from '@/utils/i18n';
import { PickingJobToolbar } from './PickingJobToolbar';
import type { PickingJobFilterDefinition, PickingJobRow } from './types';

interface PickingJobDataTableProps {
  columns: ColumnDef<PickingJobRow>[];
  data: PickingJobRow[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  isError?: boolean;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (
    updater: VisibilityState | ((prev: VisibilityState) => VisibilityState),
  ) => void;
  availableFilters: PickingJobFilterDefinition[];
  activeFilters: ActiveFilter[];
  onFilterChange: (filterId: string, value: string) => void;
  onFilterAdd: (filterId: string) => void;
  onFilterRemove: (filterId: string) => void;
  search: string;
  onSearchChange: (value: string) => void;
  /** Shown under the filters, e.g. a bound the query adds on its own. */
  filterHint?: React.ReactNode;
  header?: React.ReactNode;
  getRowHref?: (row: PickingJobRow) => string | null;
}

export function PickingJobDataTable({
  columns,
  data,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  isLoading,
  isError,
  sorting,
  onSortingChange,
  columnVisibility,
  onColumnVisibilityChange,
  availableFilters,
  activeFilters,
  onFilterChange,
  onFilterAdd,
  onFilterRemove,
  search,
  onSearchChange,
  filterHint,
  header,
  getRowHref,
}: PickingJobDataTableProps) {
  const pageCount = Math.ceil(totalCount / pageSize);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      onSortingChange(next);
    },
    onColumnVisibilityChange,
    getCoreRowModel: getCoreRowModel(),
    // Sorting and paging happen in the hook: the overview merges one result set
    // per warehouse client-side, so the table is handed a finished page.
    manualSorting: true,
    manualPagination: true,
    pageCount,
    getRowId: (row) => row.id,
  });

  return (
    <div className="tw:flex tw:flex-col tw:gap-8">
      {!!header && header}

      <PickingJobToolbar
        table={table}
        availableFilters={availableFilters}
        activeFilters={activeFilters}
        onFilterChange={onFilterChange}
        onFilterAdd={onFilterAdd}
        onFilterRemove={onFilterRemove}
        search={search}
        onSearchChange={onSearchChange}
        filterHint={filterHint}
      />

      {!isError && !isLoading && totalCount > 0 && (
        <div className="tw:text-sm tw:text-muted-foreground">
          {i18n.t('Order:resultCount', {
            shown: formatNumber(data.length),
            total: formatNumber(totalCount),
          })}
        </div>
      )}

      <div className="tw:rounded-md tw:border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((h) => (
                  <TableHead key={h.id}>
                    {h.isPlaceholder
                      ? null
                      : flexRender(h.column.columnDef.header, h.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isError ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="tw:h-24 tw:text-center tw:text-destructive"
                >
                  {i18n.t('Common:error')}
                </TableCell>
              </TableRow>
            ) : isLoading ? (
              Array.from({ length: 10 }).map((_, rowIndex) => (
                <TableRow key={`loading-row-${rowIndex}`}>
                  {table.getVisibleLeafColumns().map((column) => (
                    <TableCell key={`${column.id}-loading-${rowIndex}`}>
                      <Skeleton className="tw:h-5 tw:w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const href = getRowHref?.(row.original) ?? null;
                return (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      const content = flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      );
                      return (
                        <TableCell
                          key={cell.id}
                          className={href ? 'tw:p-0' : undefined}
                        >
                          {href ? (
                            <Link
                              to={href}
                              className="tw:block tw:h-full tw:w-full tw:cursor-pointer tw:p-2 tw:text-inherit tw:no-underline tw:hover:text-inherit tw:hover:no-underline"
                            >
                              {content}
                            </Link>
                          ) : (
                            content
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="tw:h-24 tw:text-center tw:text-muted-foreground"
                >
                  {i18n.t('Common:noResults')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <OrderPagination
        currentPage={currentPage}
        setCurrentPage={onPageChange}
        totalPages={pageCount}
      />
    </div>
  );
}
